// KITS-CAMPO (2026-06-16) — Kits de equipamento de campo.
// O patrimônio guarda itens soltos (coletora, GNSS base/rover...). Aqui o
// escritório monta "kits" (itens reais do patrimônio + acessórios livres) que
// vão a campo como uma unidade, com um QR Code pra check-in/check-out no PWA
// (fase 2) e alocação nas campanhas do Planejamento de Campo (fase 3).

import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Button, Drawer, Empty, Flex, Form, Input, InputNumber, Modal,
  Select, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, QrcodeOutlined, EditOutlined, PrinterOutlined,
  DownloadOutlined, ToolOutlined,
} from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_CFG: Record<string, { label: string; cor: string }> = {
  disponivel: { label: 'Disponível', cor: 'green' },
  em_campo:   { label: 'Em campo',   cor: 'blue' },
  manutencao: { label: 'Manutenção', cor: 'gold' },
  inativo:    { label: 'Inativo',    cor: 'default' },
};

interface KitItem { patrimonio_id: string; descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null; }
interface Acessorio { nome: string; qtd: number; }
interface Kit {
  id: string;
  nome: string;
  codigo: string | null;
  descricao: string | null;
  status: string;
  itens: KitItem[];
  acessorios: Acessorio[];
  observacoes: string | null;
  token_hash: string | null;
  // quem está com o kit agora (check-in aberto) — vem do backend
  em_campo_nome?: string | null;
  em_campo_email?: string | null;
  em_campo_desde?: string | null;
  em_campo_contrato?: string | null;
}

function fmtDM(iso?: string | null): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); }
  catch { return ''; }
}
interface Patrimonio { id: string; descricao: string; codigo_patrimonio: string | null; categoria: string | null; modelo: string | null; }

// Conteúdo gravado no QR. O leitor do PWA reconhece o prefixo e ignora QRs alheios.
const qrValor = (hash: string) => `HDKIT:${hash}`;
const itemRotulo = (p: { descricao: string; modelo?: string | null; codigo_patrimonio?: string | null }) =>
  `${p.descricao}${p.modelo ? ` ${p.modelo}` : ''}${p.codigo_patrimonio ? ` [${p.codigo_patrimonio}]` : ''}`;

async function api(action: string, extra: any = {}) {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action, ...extra }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export default function KitsCampo() {
  return <AntApp><KitsCampoInner /></AntApp>;
}

function KitsCampoInner() {
  const { message, modal } = AntApp.useApp();
  const [kits, setKits] = useState<Kit[]>([]);
  const [loading, setLoading] = useState(true);
  const [patrimonio, setPatrimonio] = useState<Patrimonio[]>([]);
  const [editando, setEditando] = useState<Kit | null>(null);
  const [aberto, setAberto] = useState(false);
  const [qrKit, setQrKit] = useState<Kit | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('ativos');
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('op_kit_listar');
      setKits(Array.isArray(d) ? d : []);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      try { const d = await api('op_kit_patrimonio'); setPatrimonio(Array.isArray(d) ? d : []); }
      catch { /* picker opcional */ }
    })();
  }, []);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return kits.filter(k => {
      if (filtroStatus === 'ativos' && k.status === 'inativo') return false;
      if (['disponivel', 'em_campo', 'manutencao', 'inativo'].includes(filtroStatus) && k.status !== filtroStatus) return false;
      if (!q) return true;
      const alvo = `${k.nome} ${k.codigo || ''} ${k.em_campo_nome || ''} ${(k.itens || []).map(i => `${i.descricao} ${i.codigo_patrimonio || ''}`).join(' ')}`.toLowerCase();
      return alvo.includes(q);
    });
  }, [kits, filtroStatus, busca]);

  async function excluir(k: Kit) {
    try { await api('op_kit_excluir', { id: k.id }); message.success('Kit excluído.'); carregar(); }
    catch (e: any) { message.error('Falha: ' + (e.response?.data?.error || e.message)); }
  }

  const colunas = [
    {
      title: 'Kit', key: 'nome',
      render: (_: any, k: Kit) => (
        <div>
          <Tooltip title="Clique para editar">
            <Text strong style={{ color: '#020D7F', cursor: 'pointer' }}
              onClick={() => { setEditando(k); setAberto(true); }}>{k.nome}</Text>
          </Tooltip>
          {k.codigo && <Tag style={{ marginLeft: 6 }}>{k.codigo}</Tag>}
          {k.descricao && <div><Text type="secondary" style={{ fontSize: 12 }}>{k.descricao}</Text></div>}
        </div>
      ),
    },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_: any, k: Kit) => {
        const c = STATUS_CFG[k.status] || STATUS_CFG.disponivel;
        return <Tag color={c.cor}>{c.label}</Tag>;
      },
    },
    {
      title: 'Com quem', key: 'portador', width: 210,
      render: (_: any, k: Kit) => {
        if (k.status !== 'em_campo' || !k.em_campo_nome) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
          <div>
            <div><Text style={{ fontSize: 13 }}>👤 {k.em_campo_nome}</Text></div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {k.em_campo_desde ? `desde ${fmtDM(k.em_campo_desde)}` : ''}
              {k.em_campo_contrato ? ` · ${k.em_campo_contrato}` : ''}
            </Text>
          </div>
        );
      },
    },
    {
      title: 'Itens', key: 'itens', width: 220,
      render: (_: any, k: Kit) => {
        const its = k.itens || [];
        const ac = k.acessorios || [];
        if (!its.length && !ac.length) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        return (
          <Tooltip title={
            <div style={{ fontSize: 12 }}>
              {its.map((i, n) => <div key={n}>• {itemRotulo(i)}</div>)}
              {ac.map((a, n) => <div key={'a' + n} style={{ opacity: 0.85 }}>◦ {a.nome}{a.qtd > 1 ? ` ×${a.qtd}` : ''}</div>)}
            </div>
          }>
            <Text style={{ fontSize: 13 }}>
              {its.length} item(s){ac.length ? ` + ${ac.length} acessório(s)` : ''}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Ações', key: 'acoes', width: 150, align: 'right' as const,
      render: (_: any, k: Kit) => (
        <Space size={2}>
          <Tooltip title="Ver/Imprimir QR">
            <Button size="small" type="text" icon={<QrcodeOutlined />} onClick={() => setQrKit(k)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => { setEditando(k); setAberto(true); }} />
          </Tooltip>
          <Tooltip title="Excluir">
            <Button size="small" type="text" danger icon={<DeleteOutlined />}
              onClick={() => modal.confirm({
                title: `Excluir o kit "${k.nome}"?`,
                content: 'Isso não apaga os itens do patrimônio, só desfaz o agrupamento.',
                okText: 'Excluir', okButtonProps: { danger: true }, onOk: () => excluir(k),
              })} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>📦 Kits de Equipamento</Title>
        <Space wrap>
          <Input.Search allowClear placeholder="Buscar kit ou item…" style={{ width: 220 }}
            onChange={e => setBusca(e.target.value)} />
          <Select value={filtroStatus} onChange={setFiltroStatus} style={{ width: 150 }}
            options={[
              { value: 'ativos', label: 'Ativos' },
              { value: 'disponivel', label: 'Disponíveis' },
              { value: 'em_campo', label: 'Em campo' },
              { value: 'manutencao', label: 'Em manutenção' },
              { value: 'inativo', label: 'Inativos' },
            ]} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditando(null); setAberto(true); }}>Novo kit</Button>
        </Space>
      </Flex>
      <Paragraph type="secondary" style={{ fontSize: 13, marginTop: -4 }}>
        Agrupe itens do patrimônio num kit que vai a campo como uma unidade. Cada kit
        ganha um QR Code pra a equipe fazer check-in/check-out pelo app.
      </Paragraph>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : filtrados.length === 0 ? (
        <Empty description="Nenhum kit. Clique em 'Novo kit' pra montar o primeiro." style={{ marginTop: 40 }} />
      ) : (
        <Table rowKey="id" size="small" dataSource={filtrados} columns={colunas} pagination={false} />
      )}

      <KitDrawer aberto={aberto} kit={editando} patrimonio={patrimonio}
        onFechar={() => setAberto(false)}
        onSalvo={() => { setAberto(false); carregar(); }} />

      <QrModal kit={qrKit} onFechar={() => setQrKit(null)} />
    </div>
  );
}

// ── Drawer de criar/editar kit ───────────────────────────────────────────────
function KitDrawer({ aberto, kit, patrimonio, onFechar, onSalvo }: {
  aberto: boolean; kit: Kit | null; patrimonio: Patrimonio[];
  onFechar: () => void; onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    form.resetFields();
    if (kit) {
      form.setFieldsValue({
        nome: kit.nome,
        codigo: kit.codigo || undefined,
        descricao: kit.descricao || undefined,
        status: kit.status,
        itens_ids: (kit.itens || []).map(i => i.patrimonio_id),
        acessorios: kit.acessorios || [],
        observacoes: kit.observacoes || undefined,
      });
    } else {
      form.setFieldsValue({ status: 'disponivel', acessorios: [], itens_ids: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, kit?.id]);

  // Opções = patrimônio ativo + (no modo edição) itens do kit que já saíram do
  // patrimônio ativo, pra não sumirem da seleção.
  const itemOptions = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const p of patrimonio) mapa.set(p.id, itemRotulo(p));
    for (const i of (kit?.itens || [])) if (!mapa.has(i.patrimonio_id)) mapa.set(i.patrimonio_id, itemRotulo(i) + ' (baixado)');
    const catDe = (id: string) => patrimonio.find(p => p.id === id)?.categoria || 'Outros';
    const cats = Array.from(new Set(Array.from(mapa.keys()).map(catDe)));
    return cats.map(cat => ({
      label: cat,
      options: Array.from(mapa.entries()).filter(([id]) => catDe(id) === cat)
        .map(([id, label]) => ({ value: id, label })),
    }));
  }, [patrimonio, kit]);

  async function salvar(v: any) {
    setSalvando(true);
    try {
      const payload = {
        nome: v.nome,
        codigo: v.codigo || null,
        descricao: v.descricao || null,
        status: v.status || 'disponivel',
        itens_ids: v.itens_ids || [],
        acessorios: (v.acessorios || []).filter((a: any) => a && a.nome),
        observacoes: v.observacoes || null,
      };
      await api(kit ? 'op_kit_atualizar' : 'op_kit_criar', kit ? { id: kit.id, ...payload } : payload);
      message.success(kit ? 'Kit atualizado.' : 'Kit criado — QR gerado.');
      onSalvo();
    } catch (e: any) {
      message.error('Falha: ' + (e.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Drawer open={aberto} onClose={onFechar} width={520} destroyOnClose
      title={kit ? '✏️ Editar kit' : 'Novo kit de equipamento'}>
      <Form form={form} layout="vertical" size="small" onFinish={salvar}>
        <Form.Item label="Nome do kit" name="nome" rules={[{ required: true, message: 'Dê um nome ao kit' }]}
          extra="Ex: Kit GNSS RTK 01">
          <Input placeholder="Kit GNSS RTK 01" />
        </Form.Item>
        <Flex gap={10}>
          <Form.Item label="Código" name="codigo" style={{ flex: 1 }} extra="Opcional — etiqueta interna">
            <Input placeholder="KIT-GNSS-01" />
          </Form.Item>
          <Form.Item label="Status" name="status" style={{ width: 170 }}>
            <Select options={Object.entries(STATUS_CFG).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
        </Flex>
        <Form.Item label="Descrição" name="descricao">
          <Input placeholder="Pra que serve este kit (opcional)" />
        </Form.Item>

        <Form.Item label="Itens do patrimônio" name="itens_ids"
          rules={[{ required: true, message: 'Escolha pelo menos um item' }]}
          extra="Itens reais do patrimônio (rastreio pelo nº de patrimônio)">
          <Select mode="multiple" showSearch optionFilterProp="label" allowClear
            placeholder="Buscar item do patrimônio…" options={itemOptions} maxTagCount="responsive" />
        </Form.Item>

        <Form.Item label="Acessórios (não-patrimoniados)" style={{ marginBottom: 8 }}
          extra="Cabos, bastão, trena, baterias… o que não tem nº de patrimônio">
          <Form.List name="acessorios">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Flex key={field.key} gap={6} align="start">
                    <ToolOutlined style={{ marginTop: 8, color: '#0930D0' }} />
                    <Form.Item name={[field.name, 'nome']} style={{ flex: 1, marginBottom: 0 }}>
                      <Input placeholder="Acessório (ex: Bastão de 2m)" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'qtd']} style={{ width: 70, marginBottom: 0 }} initialValue={1}>
                      <InputNumber min={1} style={{ width: '100%' }} />
                    </Form.Item>
                    <Button size="small" type="text" danger onClick={() => remove(field.name)}>×</Button>
                  </Flex>
                ))}
                <Button size="small" type="dashed" block onClick={() => add({ qtd: 1 })}>+ Adicionar acessório</Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="Observações" name="observacoes">
          <TextArea rows={2} placeholder="Cuidados, número de série, validade de calibração…" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={salvando} block>
          {kit ? 'Salvar' : 'Criar kit'}
        </Button>
      </Form>
    </Drawer>
  );
}

// ── Modal do QR (ver / imprimir / baixar) ────────────────────────────────────
function QrModal({ kit, onFechar }: { kit: Kit | null; onFechar: () => void }) {
  const { message } = AntApp.useApp();
  if (!kit) return null;

  function getCanvas(): HTMLCanvasElement | null {
    return document.querySelector<HTMLCanvasElement>('#kit-qr-box canvas');
  }
  function baixarPng() {
    const c = getCanvas(); if (!c) return;
    const a = document.createElement('a');
    a.href = c.toDataURL('image/png');
    a.download = `QR-${(kit!.codigo || kit!.nome).replace(/[^a-zA-Z0-9._-]+/g, '_')}.png`;
    a.click();
  }
  function imprimir() {
    const c = getCanvas(); if (!c) return;
    const img = c.toDataURL('image/png');
    const itens = (kit!.itens || []).map(i => `<li>${itemRotulo(i)}</li>`).join('');
    const ac = (kit!.acessorios || []).map(a => `<li>${a.nome}${a.qtd > 1 ? ` ×${a.qtd}` : ''}</li>`).join('');
    const w = window.open('', '_blank', 'width=420,height=620');
    if (!w) { message.warning('Permita pop-ups pra imprimir.'); return; }
    w.document.write(`
      <html><head><title>QR ${kit!.nome}</title>
      <style>
        body{font-family:Arial,sans-serif;text-align:center;padding:24px;color:#111}
        h2{margin:4px 0;font-size:20px} .cod{color:#555;font-size:13px;margin-bottom:12px}
        img{width:240px;height:240px} ul{text-align:left;font-size:12px;display:inline-block;margin:12px auto 0}
        .marca{margin-top:18px;color:#888;font-size:11px}
      </style></head><body>
        <h2>${kit!.nome}</h2>
        <div class="cod">${kit!.codigo || ''}</div>
        <img src="${img}" />
        ${itens || ac ? `<ul>${itens}${ac}</ul>` : ''}
        <div class="marca">Hydrodata · check-in/out pelo app de campo</div>
        <script>window.onload=function(){window.print()}</script>
      </body></html>`);
    w.document.close();
  }

  return (
    <Modal open={!!kit} onCancel={onFechar} title={`QR — ${kit.nome}`} footer={null} width={360}>
      <div style={{ textAlign: 'center' }}>
        {kit.token_hash ? (
          <>
            <div id="kit-qr-box" style={{ display: 'inline-block', padding: 12, background: '#fff', border: '1px solid #eee', borderRadius: 8 }}>
              <QRCodeCanvas value={qrValor(kit.token_hash)} size={220} level="M" includeMargin />
            </div>
            {kit.codigo && <div style={{ marginTop: 8 }}><Tag>{kit.codigo}</Tag></div>}
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 10 }}>
              Imprima e cole na maleta. A equipe aponta a câmera do app pra fazer check-in/check-out.
            </Paragraph>
            <Space style={{ marginTop: 4 }}>
              <Button icon={<PrinterOutlined />} onClick={imprimir}>Imprimir</Button>
              <Button icon={<DownloadOutlined />} onClick={baixarPng}>Baixar PNG</Button>
            </Space>
          </>
        ) : (
          <Text type="secondary">Este kit ainda não tem QR. Edite e salve pra gerar.</Text>
        )}
      </div>
    </Modal>
  );
}
